import { useContext, useEffect } from "react";
import { SEOContext } from "./seoContext";
import { useI18n } from "../i18n/context";
import { languageURL } from "../i18n/routes";
import { localizedSchema } from "../i18n/schema";
import VERIFIED_BUSINESS from "../data/verifiedBusiness.json";
import { LOCATIONS } from "../data/locations";
import { LOCATION_SERVICES } from "../data/locationServices";

export const DEFAULT_OG_IMAGE = "https://vvpsstaxiservices.com/og-cover.jpg";
export const socialImage = (image) => image && !image.endsWith(".svg")
    ? new URL(image, "https://vvpsstaxiservices.com").href : DEFAULT_OG_IMAGE;

// Lightweight, dependency-free SEO helper.
// Sets title, meta description, canonical, OG/Twitter, robots + JSON-LD schemas.
export default function useSEO({
    title,
    description,
    canonical,
    ogImage,
    ogType = "website",
    schema,
    keywords,
    noindex = false,
    robots,
} = {}) {
    const { language, t } = useI18n();
    const englishCanonical = languageURL(canonical, "en");
    title = t(title);
    description = t(description);
    canonical = languageURL(canonical, language);
    if (language === "hi") schema = localizedSchema(schema, t);
    const alternates = canonical && !noindex ? [{ hreflang: "en", href: englishCanonical }, { hreflang: "hi", href: languageURL(englishCanonical, "hi") }, { hreflang: "x-default", href: englishCanonical }] : [];
    const alternatesJSON = JSON.stringify(alternates);
    const collector = useContext(SEOContext);
    const image = socialImage(ogImage);
    const schemaJSON = JSON.stringify(schema || []);
    if (collector) Object.assign(collector, { title, description, canonical, ogImage: image, ogType, schema, keywords, noindex, robots, language, alternates });
    useEffect(() => {
        if (title) document.title = title;
        document.documentElement.lang = language;
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
        JSON.parse(alternatesJSON).forEach(({ hreflang, href }) => { const link = document.createElement("link"); link.rel = "alternate"; link.hreflang = hreflang; link.href = href; document.head.appendChild(link); });

        const setMeta = (name, content, attr = "name") => {
            const matches = [...document.head.querySelectorAll(`meta[${attr}="${name}"]`)];
            let el = matches.shift();
            matches.forEach((duplicate) => duplicate.remove());
            // A client-side route must not inherit the previous page's metadata.
            if (!content) { el?.remove(); return; }
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        setMeta("description", description);
        setMeta("keywords", keywords);
        setMeta("robots", globalThis.__VVPSS_PREVIEW__ ? "noindex, nofollow" : robots || (noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"));
        setMeta("og:title", title, "property");
        setMeta("og:description", description, "property");
        setMeta("og:type", ogType, "property");
        setMeta("og:locale", language === "hi" ? "hi_IN" : "en_IN", "property");
        setMeta("og:url", canonical, "property");
        setMeta("og:image", image, "property");
        setMeta("og:image:alt", title || "VVPSS Pvt. Ltd. Taxi Services", "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", title);
        setMeta("twitter:description", description);
        setMeta("twitter:image", image);
        ["width", "height", "type"].forEach((key) => document.querySelector(`meta[property="og:image:${key}"]`)?.remove());
        if (image === DEFAULT_OG_IMAGE) {
            setMeta("og:image:width", "1200", "property");
            setMeta("og:image:height", "630", "property");
            setMeta("og:image:type", "image/jpeg", "property");
        }

        // Self-referencing canonical
        const canonicalLinks = [...document.head.querySelectorAll('link[rel="canonical"]')];
        let canonicalLink = canonicalLinks.shift();
        canonicalLinks.forEach((duplicate) => duplicate.remove());
        if (canonical) {
            if (!canonicalLink) {
                canonicalLink = document.createElement("link");
                canonicalLink.rel = "canonical";
                document.head.appendChild(canonicalLink);
            }
            canonicalLink.href = canonical;
        } else canonicalLink?.remove();

        // JSON-LD schema
        //
        // The static build template (`public/index.html`) ships two
        // "identity" schemas — LocalBusiness/TaxiService and Organization —
        // tagged with `data-static-schema="business|organization"` so
        // crawlers see them without JS. If a page requests the SAME @id via
        // useSEO (e.g. Home.jsx passes localBusinessSchema/organizationSchema
        // to remain crawlable if the static shell is ever regenerated), we
        // skip re-appending here so the DOM never carries duplicate
        // definitions for the same @id.
        const staticIds = new Set();
        document
            .querySelectorAll('script[data-static-schema]')
            .forEach((n) => {
                try {
                    const parsed = JSON.parse(n.textContent);
                    if (parsed && parsed["@id"]) staticIds.add(parsed["@id"]);
                } catch (_) {
                    /* malformed static schema — ignore */
                }
            });

        const removeSchemas = () => {
            document
                .querySelectorAll('script[data-seo-schema="true"]')
                .forEach((n) => n.remove());
        };
        removeSchemas();
        const parsedSchema = JSON.parse(schemaJSON);
        const schemas = Array.isArray(parsedSchema) ? parsedSchema : [parsedSchema];
        schemas.forEach((s) => {
            if (!s || typeof s !== "object") return;
            if (s && s["@id"] && staticIds.has(s["@id"])) return; // dedupe
            const script = document.createElement("script");
            script.type = "application/ld+json";
            script.dataset.seoSchema = "true";
            script.text = JSON.stringify(s);
            document.head.appendChild(script);
        });

        return () => {
            removeSchemas();
        };
    }, [title, description, canonical, image, ogType, keywords, noindex, robots, schemaJSON, language, alternatesJSON]);
}

/**
 * LocalBusiness + TaxiService schema.
 *
 * IMPORTANT: We intentionally OMIT `aggregateRating` and `review` fields.
 * Per Google's structured-data policy, self-serving Review / AggregateRating
 * markup added by the business owner is a violation and may result in a
 * Manual Action. When genuine third-party Google Reviews are available, they
 * must be sourced from the actual GMB profile — never hand-authored.
 */
export const localBusinessSchema = (config) => ({
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": "https://vvpsstaxiservices.com/#business",
    name: VERIFIED_BUSINESS.company_name,
    image: "https://vvpsstaxiservices.com/og-cover.jpg",
    telephone: config?.phone || "+919425712055",
    email: config?.email || "vvpsspvtltdtaxiservices@gmail.com",
    address: {
        "@type": "PostalAddress",
        streetAddress: VERIFIED_BUSINESS.street_address,
        addressLocality: config?.city || "Satna",
        addressRegion: config?.state || "Madhya Pradesh",
        postalCode: config?.pincode || "485001",
        addressCountry: "IN",
    },
    geo: { "@type": "GeoCoordinates", latitude: VERIFIED_BUSINESS.latitude, longitude: VERIFIED_BUSINESS.longitude },
    hasMap: VERIFIED_BUSINESS.gmb_url,
    areaServed: [...new Set(LOCATIONS.map((location) => location.state).filter(Boolean))].map((name) => ({ "@type": "State", name })),
    priceRange: "₹₹",
    openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
    },
    sameAs: [
        VERIFIED_BUSINESS.gmb_url,
        config?.facebook_url,
        config?.youtube_url,
    ].filter(Boolean),
    parentOrganization: { "@id": "https://vvpsstaxiservices.com/#organization" },
    url: "https://vvpsstaxiservices.com/",
});

/**
 * Organization schema — separate identity from the LocalBusiness/TaxiService
 * node. Google merges both by @id, and we cross-link them via `parentOrganization`
 * so the knowledge graph treats them as ONE business.
 */
export const organizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://vvpsstaxiservices.com/#organization",
    name: "VVPSS Pvt. Ltd. Taxi Services",
    url: "https://vvpsstaxiservices.com/",
    logo: "https://vvpsstaxiservices.com/logo.png?v=badge-e75c0e4c89f4",
    telephone: "+919425712055",
    contactPoint: {
        "@type": "ContactPoint",
        telephone: "+919425712055",
        contactType: "customer service",
        areaServed: "IN",
        availableLanguage: ["en", "hi"],
    },
    sameAs: [VERIFIED_BUSINESS.gmb_url],
    subOrganization: { "@id": "https://vvpsstaxiservices.com/#business" },
});

export const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
});

export const serviceSchema = ({ name, description, url, areaServed }) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    provider: {
        "@type": "LocalBusiness",
        "@id": "https://vvpsstaxiservices.com/#business",
        name: "VVPSS Pvt. Ltd. Taxi Services",
    },
    areaServed: areaServed || [{ "@type": "State", name: "Madhya Pradesh" }, { "@type": "State", name: "Uttar Pradesh" }],
    description,
    url,
});

// Services and vehicle options cover all listed locations; the corporate office is in Satna.
// Keep one business identity; listing an area does not claim a branch there.
const serviceArea = (location) => ({
    "@type": "Place",
    name: location.name,
    address: { "@type": "PostalAddress", addressRegion: location.state, addressCountry: "IN" },
});

export const multiLocationFleetSchema = ({ vehicles, url }) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#fleet-hire`,
    name: "Taxi & Tempo Traveller Services Across Multiple States",
    serviceType: LOCATION_SERVICES.map((service) => service.title),
    url,
    areaServed: LOCATIONS.map(serviceArea),
    description: "Local rides, outstation journeys, airport and railway transfers, and group travel across our listed service locations. Corporate office: Satna, Madhya Pradesh. Confirm your pickup, vehicle availability and complete fare before booking.",
    provider: { "@id": "https://vvpsstaxiservices.com/#business" },
    hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Cars, SUVs and Tempo Travellers with driver",
        itemListElement: vehicles.map((vehicle) => ({
            "@type": "Offer",
            name: `${vehicle.name} hire with driver`,
            url: `https://vvpsstaxiservices.com/${vehicle.slug}`,
            itemOffered: { "@type": "Service", "@id": `https://vvpsstaxiservices.com/${vehicle.slug}#vehicle-hire-service` },
        })),
    },
});

export const cityFleetSchema = ({ location, vehicles, url }) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#fleet-hire`,
    name: `Taxi, car and Tempo Traveller hire in ${location.name}`,
    serviceType: LOCATION_SERVICES.map((service) => service.title),
    url,
    areaServed: serviceArea(location),
    description: `Enquire about the listed services and vehicle options for ${location.name}. Availability depends on your travel dates, route and vehicle. Road hire covers permitted, road-accessible parts of the itinerary, including agreed pickup points or roadheads. Our corporate office is in Satna. Confirm vehicle suitability, pickup details and the complete fare before booking.`,
    provider: { "@id": "https://vvpsstaxiservices.com/#business" },
    hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `Vehicles for ${location.name} rental enquiries`,
        itemListElement: vehicles.map((vehicle) => ({
            "@type": "Offer",
            name: `${vehicle.name} hire with driver`,
            url: `https://vvpsstaxiservices.com/${vehicle.slug}`,
            itemOffered: {
                "@type": "Service",
                "@id": `https://vvpsstaxiservices.com/${vehicle.slug}#vehicle-hire-service`,
                name: `${vehicle.name} Hire with Driver`,
                url: `https://vvpsstaxiservices.com/${vehicle.slug}`,
            },
        })),
    },
});

export const breadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: it.url,
    })),
});

export const vehicleSchema = (v) => {
 const site = "https://vvpsstaxiservices.com";
 const url = site + "/" + v.slug;
 const t = v.localTariff;
 const s = { "@context":"https://schema.org", "@type":"Service", "@id":url+"#vehicle-hire-service", name:v.name+" Hire with Driver", url, mainEntityOfPage:url, serviceType:"Chauffeur-driven local and outstation vehicle hire", description:[v.ideal, v.seatsIncludingDriver ? v.seatsIncludingDriver+" seats including driver." : "", v.fuel, v.ac ? "AC." : "", v.music ? "Music system." : "", v.roofCarrier ? "Roof luggage carrier." : ""].filter(Boolean).join(" "), provider:{"@type":"LocalBusiness","@id":site+"/#business",name:"VVPSS Pvt. Ltd. Taxi Services",url:site} };
 s.areaServed = LOCATIONS.map(serviceArea);
 s.description += " This vehicle and all listed services are offered across all service locations. Our corporate office is in Satna. Confirm availability for your travel dates, pickup details and the complete fare before booking.";
 if(v.image && !/\.svg(?:$|\?)/i.test(v.image) && !/placeholder|og[-_]cover/i.test(v.image)) s.image = new URL(v.image,site).href;
 if(!t) return s;
 const price = (amount,unitText,unitCode) => ({"@type":"UnitPriceSpecification",price:amount,priceCurrency:"INR",valueAddedTaxIncluded:false,referenceQuantity:{"@type":"QuantitativeValue",value:1,unitText,...(unitCode?{unitCode}:{})}});
 const extra=(name,amount,unitText,unitCode)=>({"@type":"Offer",name,url,priceSpecification:price(amount,unitText,unitCode)});
 const night=extra("Applicable night charge",t.nightCharge,"night");
 const exclusions="5% GST extra applies to all bookings. Listed rates exclude GST. Toll, parking, border charges, night charges and driver meal are extra.";
 s.offers=[{"@type":"Offer",name:v.name+": local 120 KM / 12 hour base package",url,description:"Fuel extra: "+t.billingFuel+" at 1 litre per "+t.fuelKmPerLitre+" KM, minimum 120 KM for fuel billing. No separate per-KM hire charge applies beyond 120 KM; fuel is billed for actual running. Extra hours beyond 12 are charged separately. "+exclusions+(v.luxuryModified?" Other road expenses are also extra for local Traveller bookings.":""),priceSpecification:price(t.baseFare,"120 KM / 12 hour package; fuel extra"),addOn:[extra("Local duty beyond 12 hours",t.extraHour,"hour","HUR"),night]}, {"@type":"Offer",name:v.name+": outstation hire per kilometre",url,description:"Fuel included. Minimum billing: 300 KM per day. "+exclusions,eligibleQuantity:{"@type":"QuantitativeValue",minValue:300,unitCode:"KMT",unitText:"billable kilometres per day"},priceSpecification:price(v.pricePerKm,"kilometre","KMT"),addOn:[night]}];
 return s;
};
